// Limits and validation shared by server (authoritative) and client (early
// feedback). The server re-checks everything; the client only uses these to
// disable a button before a round trip.

export const LIMITS = {
  nameMax: 40,
  emailMax: 254,
  chatMax: 500,
  questionMax: 300,
  answerMax: 1000,
  pollQuestionMax: 200,
  pollOptionMax: 80,
  pollOptionsMin: 2,
  pollOptionsMax: 6,
  ctaTitleMax: 80,
  ctaDetailMax: 160,
  ctaButtonMax: 32,
  urlMax: 2000,
  titleMax: 120,
  descriptionMax: 2000,
  slugMax: 60,
  uploadMaxBytes: 2 * 1024 ** 3,
  deckMaxPages: 300,
  videoMaxSeconds: 3 * 60 * 60,
  chatPreviewMax: 8,
} as const

/** Emoji a named attendee can float over the stage. */
export const ROOM_REACTIONS = ['👏', '❤️', '😂', '🎉', '👍', '🔥', '🤯', '💡'] as const

/** Emoji for reacting to one chat message. */
export const MESSAGE_REACTIONS = ['👍', '❤️', '😂', '👏', '🎉', '🤔'] as const

/** Room reactions: sustained rate and burst per person. */
export const REACTION_RATE = { perSecond: 2, burst: 6 } as const

/**
 * A start time that has passed can't be scheduled or moved to. A quarter of an
 * hour of grace, so "starting now" still works.
 */
export const PAST_START_GRACE_MS = 15 * 60_000
export function startHasPassed(iso: string | null | undefined, now = Date.now()): boolean {
  return !!iso && Date.parse(iso) < now - PAST_START_GRACE_MS
}
export const PAST_START_MESSAGE = 'That time has passed — pick a date and time from now on'

/** Two poll choices voters couldn't tell apart (case and spacing aside): the second one's index, or -1. */
export function duplicateOption(labels: string[]): number {
  const seen = new Set<string>()
  for (let i = 0; i < labels.length; i++) {
    const k = labels[i].normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase()
    if (!k) continue
    if (seen.has(k)) return i
    seen.add(k)
  }
  return -1
}
export const DUPLICATE_OPTION_MESSAGE = 'Two options say the same thing — make each one different'

/** Paths that can never be an event slug (they are app routes). */
export const RESERVED_SLUGS = new Set([
  'api', 'ws', 'assets', 'auth', 'events', 'join', 'r', 'record', 'studio',
  'health', 'healthz', 'static', 'favicon.ico', 'robots.txt', 'go', 'admin',
  'new', 'login', 'logout', 'signin', 'replay', 'media', 'gifs', 'fonts', 'analytics',
])

export function isValidSlug(slug: string): boolean {
  return slug.length >= 3 && slug.length <= LIMITS.slugMax &&
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) && !RESERVED_SLUGS.has(slug)
}

export function slugify(title: string): string {
  return title.toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .slice(0, LIMITS.slugMax).replace(/-+$/g, '')
}

/** Collapse whitespace, strip control characters. Returns '' for nothing left. */
export function cleanText(input: unknown, max: number): string {
  if (typeof input !== 'string') return ''
  // eslint-disable-next-line no-control-regex
  const s = input.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u200B-\u200F\u2028\u2029\uFEFF]/g, '')
  return s.replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim().slice(0, max)
}

export function cleanName(input: unknown): string {
  return cleanText(input, LIMITS.nameMax).replace(/\s+/g, ' ')
}

/** An attendee's email: '' when none was given, null when it doesn't look like one. */
export function cleanEmail(input: unknown): string | null {
  const v = typeof input === 'string' ? input.trim().toLowerCase() : ''
  if (!v) return ''
  return v.length <= LIMITS.emailMax && /^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/.test(v) ? v : null
}
export const EMAIL_MESSAGE = 'That email doesn’t look right. Check it, or leave it empty.'

/**
 * CTA and resource links: absolute http(s) only, no credentials in the URL.
 * A bare domain ("gryd.io/review") is accepted and given https://.
 */
export function normalizeUrl(input: unknown): string | null {
  if (typeof input !== 'string') return null
  let raw = input.trim()
  if (!raw || raw.length > LIMITS.urlMax) return null
  if (!/^[a-z][a-z0-9+.-]*:/i.test(raw)) raw = 'https://' + raw
  let u: URL
  try { u = new URL(raw) } catch { return null }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null
  if (u.username || u.password) return null
  if (!u.hostname.includes('.') && u.hostname !== 'localhost') return null
  return u.toString()
}

/**
 * Links in the chat: only the webinar's team (hosts, moderators, presenters)
 * may post one (Josh, 10-01). A link is an http(s):// or www. address; a bare
 * "acme.com" in an attendee's message is just text, never made clickable.
 */
export const CHAT_LINKS_MESSAGE = 'Only the webinar team can share links in the chat'
const LINK = /\b(?:https?:\/\/|www\.)[^\s<>"']+/i
export function hasLink(text: string): boolean {
  return LINK.test(text)
}

/** The team's links, for making them clickable: addresses plus bare domains on common endings. */
const TEAM_LINK = /\b(?:https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|(?<![@.\w-])[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|io|net|org|co|ai|app|dev|me|us|uk|ca|au|de|so|to|tv|ly|gl)(?![a-z0-9-])(?:\/[^\s<>"']*)?)/gi

/** Text split into plain runs and links (with a trailing full stop or bracket left out of the link). */
export function splitLinks(text: string): { text: string; href?: string }[] {
  const out: { text: string; href?: string }[] = []
  let last = 0
  for (const m of text.matchAll(TEAM_LINK)) {
    let link = m[0]
    const trail = /[.,;:!?)\]]+$/.exec(link)?.[0] ?? ''
    link = link.slice(0, link.length - trail.length)
    const href = normalizeUrl(link)
    if (!href) continue
    if (m.index! > last) out.push({ text: text.slice(last, m.index) })
    out.push({ text: link, href })
    last = m.index! + link.length
  }
  if (last < text.length) out.push({ text: text.slice(last) })
  return out
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}
